<script lang="ts" setup>
import { type Component, toRef } from "vue";
import { mergeConfig } from "#ui/utils";
import appConfig from "#build/app.config";
import { select as selectConfig, selectMenu } from "#ui/ui.config";
import { useUI } from "#ui/composables/useUI";

const props = withDefaults(
    defineProps<{
        items: any[];
        select: (item: any) => void;
        itemComponent: Component | string;
        matchKey: string;
        query: string;
        selectIcon?: string;
        ui?: any;
        uiMenu?: any;
        class?: any;
    }>(),
    {
        query: "",
        ui: () => ({}),
        uiMenu: () => ({}),
        class: "",
        items: () => [],
    },
);

const selectedIndex = ref<number>(0);
const listBox = ref();
const config = mergeConfig(
    appConfig.ui.strategy,
    appConfig.ui.select,
    selectConfig,
);
const configMenu = mergeConfig(
    appConfig.ui.strategy,
    appConfig.ui.selectMenu,
    selectMenu,
);
const { ui } = useUI(
    "select",
    toRef(props, "ui"),
    config,
    toRef(props, "class"),
);
const { ui: uiMenu } = useUI("selectMenu", toRef(props, "uiMenu"), configMenu);
const container = ref<HTMLElement>();

watch(props.items, () => {
    selectedIndex.value = 0;
});

function onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === "ArrowUp") {
        upHandler();
        return true;
    }

    if (event.key === "ArrowDown") {
        downHandler();
        return true;
    }

    if (event.key === "Enter") {
        enterHandler();
        return true;
    }

    return false;
}

function upHandler() {
    selectedIndex.value =
        (selectedIndex.value + props.items.length - 1) % props.items.length;
    scrollHandler();
}

function downHandler() {
    selectedIndex.value = (selectedIndex.value + 1) % props.items.length;
    scrollHandler();
}

function enterHandler() {
    selectItem(selectedIndex.value);
}

function isElementVisibleInScrollContainer(
    element: HTMLElement,
    container: HTMLElement,
) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return (
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom &&
        elementRect.left >= containerRect.left &&
        elementRect.right <= containerRect.right
    );
}

function scrollHandler() {
    nextTick(() => {
        const element = container.value?.querySelector(
            '[aria-selected="true"]',
        );

        if (
            element &&
            !isElementVisibleInScrollContainer(
                element as HTMLElement,
                element.parentElement as HTMLElement,
            )
        )
            element?.scrollIntoView();
    });
}

function selectItem(index: number) {
    const item = props.items[index];

    if (item) {
        props.select(item);
    }
}

defineExpose({
    onKeyDown,
});

const shallowItems = shallowRef((props.items ?? []).slice(0, 10));
const listBoxEl = computed(() => listBox.value?.$el);
useInfiniteScroll(
    listBoxEl,
    () => {
        shallowItems.value = [
            ...shallowItems.value,
            ...(props.items ?? []).slice(
                shallowItems.value.length,
                10 + shallowItems.value.length,
            ),
        ];
    },
    {
        distance: 150,
        canLoadMore: () => {
            return shallowItems.value.length < props.items?.length;
        },
    },
);

watch(
    () => props.items,
    (to) => {
        shallowItems.value = (to ?? []).slice(0, 10);
    },
);

function onError(error: Error) {
    console.error(error);
}

export interface SuggestionListItemProps<T = any> {
    context: {
        index: number;
        item: T;
        select: () => void;
        active: boolean;
        ui: typeof uiMenu;
    };
}
</script>

<template>
    <div>
        <NuxtErrorBoundary @error="onError">
            <HeadlessListbox
                :open="true"
                :model-value="selectedIndex"
                as="div"
                :class="ui.wrapper"
                @update:model-value="selectItem"
            >
                <div ref="container" :class="[uiMenu.container, uiMenu.width]">
                    <Transition appear v-bind="uiMenu.transition">
                        <div>
                            <HeadlessListboxOptions
                                static
                                :class="[
                                    uiMenu.base,
                                    uiMenu.ring,
                                    uiMenu.rounded,
                                    uiMenu.shadow,
                                    uiMenu.background,
                                    uiMenu.padding,
                                    uiMenu.height,
                                ]"
                                ref="listBox"
                            >
                                <template v-if="items.length">
                                    <HeadlessListboxOption
                                        v-for="(item, index) in shallowItems"
                                        :key="index"
                                        v-slot="{
                                            active,
                                            selected,
                                            disabled: optionDisabled,
                                        }"
                                        v-memo="[item.key]"
                                        as="template"
                                        :value="index"
                                        :disabled="item.disabled"
                                    >
                                        <li
                                            :class="[
                                                'w-full hover:bg-gray-100',
                                                uiMenu.option.base,
                                                uiMenu.option.rounded,
                                                uiMenu.option.padding,
                                                uiMenu.option.size,
                                                uiMenu.option.color,
                                                active
                                                    ? uiMenu.option.active
                                                    : uiMenu.option.inactive,
                                                selectedIndex === index
                                                    ? 'bg-gray-100'
                                                    : undefined,
                                                optionDisabled &&
                                                    uiMenu.option.disabled,
                                            ]"
                                        >
                                            <div
                                                :class="[
                                                    uiMenu.option.container,
                                                    'flex-1',
                                                ]"
                                            >
                                                <Component
                                                    :is="itemComponent"
                                                    v-if="itemComponent"
                                                    :context="{
                                                        index,
                                                        item,
                                                        select: () =>
                                                            selectItem(index),
                                                        active: selected,
                                                        ui: uiMenu,
                                                    }"
                                                />
                                                <span
                                                    v-else
                                                    class="flex items-start gap-2 w-full flex-1"
                                                >
                                                    <UiIcon
                                                        v-if="item.icon"
                                                        :icon="item.icon"
                                                        :class="[
                                                            uiMenu.option.icon
                                                                .base,
                                                            active
                                                                ? uiMenu.option
                                                                      .icon
                                                                      .active
                                                                : uiMenu.option
                                                                      .icon
                                                                      .inactive,
                                                            item.iconClass,
                                                        ]"
                                                        aria-hidden="true"
                                                    />

                                                    <UIAvatar
                                                        v-else-if="item.avatar"
                                                        v-bind="{
                                                            size: uiMenu.option
                                                                .avatar.size,
                                                            ...item.avatar,
                                                        }"
                                                        :class="
                                                            uiMenu.option.avatar
                                                                .base
                                                        "
                                                        aria-hidden="true"
                                                    />
                                                    <span
                                                        v-else-if="item.chip"
                                                        :class="
                                                            uiMenu.option.chip
                                                                .base
                                                        "
                                                        :style="{
                                                            background: `#${item.chip}`,
                                                        }"
                                                    />
                                                    <span
                                                        class="flex items-start flex-col gap-1 flex-1"
                                                    >
                                                        <span
                                                            class="font-medium text-sm truncate"
                                                        >
                                                            <Mark
                                                                :text="
                                                                    item.label
                                                                        ? item.label
                                                                              .toString()
                                                                              .replaceAll(
                                                                                  ' : ',
                                                                                  ': ',
                                                                              )
                                                                        : item.label
                                                                "
                                                            />
                                                        </span>
                                                        <span
                                                            class="opacity-75"
                                                        >
                                                            <Mark
                                                                :text="`\{\{${item.key}\}\}`"
                                                                :keyword="query"
                                                            />
                                                        </span>
                                                    </span>
                                                    <UBadge
                                                        color="gray"
                                                        :label="
                                                            item?.type ?? ''
                                                        "
                                                        class="rounded-full"
                                                    />
                                                </span>
                                            </div>
                                        </li>
                                    </HeadlessListboxOption>
                                </template>
                            </HeadlessListboxOptions>
                        </div>
                    </Transition>
                </div>
            </HeadlessListbox>
        </NuxtErrorBoundary>
    </div>
</template>
